import React, { useState } from 'react';
import { 
  BarChart3, Users, Package, ShoppingCart, DollarSign, TrendingUp, 
  Eye, MessageSquare, AlertTriangle, CheckCircle, Clock, Star,
  ArrowUp, ArrowDown, Filter, Download, Search, Bell, Settings,
  Globe, Shield, Truck, CreditCard, BarChart, Activity
} from 'lucide-react';
import { X } from 'lucide-react';

import { GoogleAnalytics } from '../../lib/analytics';

const AdminDashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');

  // Google Analytics management
  const [analyticsEnabled, setAnalyticsEnabled] = useState(GoogleAnalytics.isAnalyticsEnabled());
  const [trackingId, setTrackingId] = useState(GoogleAnalytics.getTrackingId());
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [newTrackingId, setNewTrackingId] = useState(trackingId);

  const handleToggleAnalytics = () => {
    const newState = !analyticsEnabled;
    setAnalyticsEnabled(newState);
    GoogleAnalytics.setEnabled(newState);
    
    // Store preference in localStorage
    localStorage.setItem('analytics_enabled', newState.toString());
  };

  const handleUpdateTrackingId = () => {
    if (newTrackingId && newTrackingId !== trackingId) {
      GoogleAnalytics.updateTrackingId(newTrackingId);
      setTrackingId(newTrackingId);
      localStorage.setItem('ga_tracking_id', newTrackingId);
      setShowAnalyticsModal(false);
      
      // Show success message
      alert('Google Analytics tracking ID updated successfully!');
    }
  };

  // Load analytics preferences on component mount
  React.useEffect(() => {
    const savedEnabled = localStorage.getItem('analytics_enabled');
    const savedTrackingId = localStorage.getItem('ga_tracking_id');
    
    if (savedEnabled !== null) {
      const enabled = savedEnabled === 'true';
      setAnalyticsEnabled(enabled);
      GoogleAnalytics.setEnabled(enabled);
    }
    
    if (savedTrackingId) {
      setTrackingId(savedTrackingId);
      setNewTrackingId(savedTrackingId);
      GoogleAnalytics.updateTrackingId(savedTrackingId);
    }
  }, []);

  const stats = [
    {
      title: 'Total Revenue',
      value: '$124,563',
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Active Users',
      value: '8,549',
      change: '+8.2%',
      trend: 'up',
      icon: Users,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Total Products',
      value: '2,847',
      change: '+15.3%',
      trend: 'up',
      icon: Package,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Orders',
      value: '1,234',
      change: '-2.1%',
      trend: 'down',
      icon: ShoppingCart,
      color: 'text-orange-600 bg-orange-100'
    }
  ];

  const recentActivities = [
    {
      type: 'user_registration',
      message: 'New supplier registered: Bengal Textiles Ltd.',
      time: '2 minutes ago',
      icon: Users,
      color: 'text-blue-600'
    },
    {
      type: 'product_added',
      message: 'New product added: Premium Cotton Shirt',
      time: '15 minutes ago',
      icon: Package,
      color: 'text-green-600'
    },
    {
      type: 'order_placed',
      message: 'Order #12345 placed by John Doe',
      time: '1 hour ago',
      icon: ShoppingCart,
      color: 'text-purple-600'
    },
    {
      type: 'verification_pending',
      message: 'Supplier verification pending: Eco Jute Co.',
      time: '2 hours ago',
      icon: AlertTriangle,
      color: 'text-yellow-600'
    },
    {
      type: 'payment_received',
      message: 'Payment received for order #12340',
      time: '3 hours ago',
      icon: DollarSign,
      color: 'text-green-600'
    }
  ];

  const topProducts = [
    { name: 'Premium Cotton T-Shirt', sales: 245, revenue: '$7,350', trend: 'up' },
    { name: 'Handwoven Jute Bag', sales: 189, revenue: '$3,780', trend: 'up' },
    { name: 'Traditional Jamdani Saree', sales: 156, revenue: '$14,040', trend: 'down' },
    { name: 'Leather Wallet', sales: 134, revenue: '$4,020', trend: 'up' },
    { name: 'Organic Cotton Polo', sales: 98, revenue: '$2,450', trend: 'up' }
  ];

  const pendingActions = [
    { type: 'Supplier Verification', count: 12, priority: 'high' },
    { type: 'Product Approval', count: 28, priority: 'medium' },
    { type: 'Customer Support', count: 15, priority: 'high' },
    { type: 'Payment Disputes', count: 3, priority: 'critical' },
    { type: 'Content Moderation', count: 7, priority: 'low' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Welcome back, Admin</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
              >
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${
                    stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.trend === 'up' ? (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    <span>{stat.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Revenue Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Revenue Overview</h2>
                <div className="flex items-center space-x-2">
                  <button className="text-sm text-gray-600 hover:text-gray-900">Revenue</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Orders</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Users</button>
                </div>
              </div>
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Chart visualization would go here</p>
                  <p className="text-sm text-gray-500">Integration with charting library needed</p>
                </div>
              </div>
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Top Performing Products</h2>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">View All</button>
              </div>
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600">{product.sales} sales</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{product.revenue}</p>
                      <div className={`flex items-center text-sm ${
                        product.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {product.trend === 'up' ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowDown className="h-3 w-3 mr-1" />
                        )}
                        <span>{product.trend === 'up' ? '+' : '-'}5%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Pending Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Pending Actions</h2>
              <div className="space-y-4">
                {pendingActions.map((action, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{action.type}</p>
                      <p className="text-sm text-gray-600">{action.count} items</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      action.priority === 'critical' ? 'bg-red-100 text-red-700' :
                      action.priority === 'high' ? 'bg-yellow-100 text-yellow-700' :
                      action.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {action.priority}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h2>
              <div className="space-y-4">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${activity.color.replace('text-', 'bg-').replace('-600', '-100')}`}>
                      <activity.icon className={`h-4 w-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Quick Stats</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-700">Countries Served</span>
                  </div>
                  <span className="font-semibold text-gray-900">45</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Verified Suppliers</span>
                  </div>
                  <span className="font-semibold text-gray-900">156</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Truck className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-gray-700">Active Shipments</span>
                  </div>
                  <span className="font-semibold text-gray-900">89</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-orange-600" />
                    <span className="text-sm text-gray-700">Pending Payments</span>
                  </div>
                  <span className="font-semibold text-gray-900">23</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;